import { AdElementWidget, resolveWidgetUrl } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import { FC, useMemo } from 'react';

type Props = {
  id: string;
  name: string;
  data: AdElementWidget['data'];
  position: AdElementWidget['position'];
  size: AdElementWidget['size'];
  rotation?: AdElementWidget['rotation'];
  isPreview?: boolean;
  animationStyle?: ElementAnimationStyle;
  animationTargetId?: string;
  /**
   * The widget page with its data already written in, fetched on the server. When set, the page
   * is drawn from this instead of being loaded from the widget host by the viewer's browser.
   */
  srcDoc?: string;
};

/**
 * Draws a widget layer as an iframe. A widget whose url fails `resolveWidgetUrl` draws nothing
 * in a served ad; in the editor and preview (`isPreview`) it is drawn as a dashed outline, so
 * the layer can still be seen and selected before its link is set.
 * The iframe always ignores the pointer, because a cross-origin or sandboxed page would swallow
 * the click: in the editor and preview that click selects and moves the layer, and in a served
 * ad it must fall through to the layer underneath and bubble to the ad-server's click-through
 * listener on `#react-root`.
 * With `srcDoc` the page is drawn from that markup inside a scripts-only sandbox: a srcdoc
 * document would otherwise run with the ad page's own origin, while a page loaded from the
 * widget host is already kept apart from the ad by its origin.
 */
const WidgetElement: FC<Props> = ({
  id,
  name,
  data,
  position,
  size,
  rotation,
  isPreview,
  animationStyle,
  animationTargetId,
  srcDoc,
}) => {
  const resolved = useMemo(() => resolveWidgetUrl(data), [data]);

  const transform =
    [animationStyle?.transform, rotation ? `rotate(${rotation}rad)` : undefined]
      .filter(Boolean)
      .join(' ') || undefined;

  if (!resolved.ok) {
    if (!isPreview) return null;

    return (
      <div
        id={id}
        data-animation-target-id={animationTargetId}
        data-widget-placeholder=""
        style={{
          position: 'absolute',
          left: position.x,
          top: position.y,
          width: size.width,
          height: size.height,
          zIndex: position.z,
          boxSizing: 'border-box',
          border: '2px dashed #8a94a6',
          background: 'rgba(138, 148, 166, 0.15)',
          pointerEvents: 'none',
          transform,
          opacity: animationStyle?.opacity,
        }}
      />
    );
  }

  return (
    <iframe
      id={id}
      data-animation-target-id={animationTargetId}
      {...(srcDoc === undefined ? { src: resolved.url } : { srcDoc, sandbox: 'allow-scripts' })}
      title={name || 'Widget'}
      loading="eager"
      width={size.width}
      height={size.height}
      style={{
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex: position.z,
        border: 0,
        display: 'block',
        pointerEvents: 'none',
        transform,
        opacity: animationStyle?.opacity,
      }}
    />
  );
};

WidgetElement.displayName = 'WidgetElement';

export default WidgetElement;
